import { useMemo } from "react";
import { t } from "ttag";
import _ from "underscore";

import { useCacheConfigs } from "metabase/admin/performance/hooks/useCacheConfigs";
import { getShortStrategyLabel } from "metabase/admin/performance/utils";
import { getFormattedTime } from "metabase/common/components/DateTime/DateTime";
import { DelayedLoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper/DelayedLoadingAndErrorWrapper";
import { getRelativeTime } from "metabase/lib/time-dayjs";
import type { SidebarCacheSectionProps } from "metabase/plugins";
import { Anchor, Flex, Stack, Text, Tooltip } from "metabase/ui";

import Styles from "./CachingPluginComponents.module.css";
import { getItemId } from "./utils";

/** Renders a timestamp as relative time ("5 minutes ago" / "in 12 minutes"), with the full
 * datetime in the user's local timezone shown on hover. Both use the browser's local timezone. */
const RelativeTime = ({ timestamp }: { timestamp: string }) => (
  <Tooltip label={getFormattedTime(timestamp)}>
    <Text span fw="bold" c="text-dark">
      {getRelativeTime(timestamp)}
    </Text>
  </Tooltip>
);

/** Displays the current cache invalidation strategy and provides a button that opens the cache configuration form */
export const SidebarCacheSection = ({
  item,
  model,
  setPage,
}: SidebarCacheSectionProps) => {
  const id = useMemo(() => getItemId(model, item), [model, item]);
  const configurableModels = useMemo(() => [model], [model]);

  const { configs, loading, error } = useCacheConfigs({
    configurableModels,
    id,
  });

  const targetConfig = useMemo(() => {
    const id = getItemId(model, item);
    return _.findWhere(configs, { model, model_id: id });
  }, [configs, model, item]);
  const savedStrategy = targetConfig?.strategy;

  const shortStrategyLabel =
    getShortStrategyLabel(savedStrategy, model) || t`Use default`;
  const labelId = "question-caching-policy-label";

  // Only items with an automatic (preemptive) refresh policy have a last/next refresh to show.
  const isPreemptive =
    (savedStrategy?.type === "schedule" ||
      savedStrategy?.type === "duration") &&
    savedStrategy.refresh_automatically === true;

  const refreshStatus = targetConfig?.refresh_status;
  const lastRefreshedAt = refreshStatus?.finished_at;
  const nextRunAt = targetConfig?.next_run_at;

  return (
    <DelayedLoadingAndErrorWrapper delay={0} loading={loading} error={error}>
      <Stack gap="sm">
        <Flex align="center" justify="space-between">
          <span id={labelId}>{t`When to get new results`}</span>
          <Anchor
            className={Styles.SidebarFormLauncher}
            role="button"
            onClick={() => setPage("caching")}
            aria-labelledby={labelId}
          >
            {shortStrategyLabel}
          </Anchor>
        </Flex>
        {isPreemptive && (
          <Stack gap="xs">
            <Flex align="center" justify="space-between" gap="md">
              <Text c="text-medium">{t`Last refreshed`}</Text>
              {refreshStatus?.status === "running" ? (
                <Text span fw="bold" c="text-dark">{t`Refreshing now…`}</Text>
              ) : lastRefreshedAt ? (
                <RelativeTime timestamp={lastRefreshedAt} />
              ) : (
                <Text span c="text-light">{t`Not yet refreshed`}</Text>
              )}
            </Flex>
            <Flex align="center" justify="space-between" gap="md">
              <Text c="text-medium">{t`Next refresh`}</Text>
              {nextRunAt ? (
                <RelativeTime timestamp={nextRunAt} />
              ) : (
                <Text span c="text-light">{t`Calculating…`}</Text>
              )}
            </Flex>
          </Stack>
        )}
      </Stack>
    </DelayedLoadingAndErrorWrapper>
  );
};
